import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';
const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;
const EMPTY = { name: '', slug: '', description: '', image_url: '', sort_order: 0, is_active: 1 };

const labelClass = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5';
const inputClass = 'w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary';

// Manages one brand's two-level catalog: categories ("Machine Cutting Tools")
// and each category's sub categories ("Disc Cutting"). Shown publicly at
// /brands/<brand>/<category>/<sub category>, ending in a Contact Us button.
export default function AdminBrandCategories({ brand, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // null = tree view; otherwise { mode: 'add' | 'edit', id?, parent? }
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    API.get('/brand-categories', { params: { brand_id: brand.id } })
      .then(r => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [brand.id]); // eslint-disable-line

  const categories = rows.filter(r => !r.parent_id);
  const childrenOf = (id) => rows.filter(r => r.parent_id === id);

  const openAdd = (parent = null) => {
    const siblings = parent ? childrenOf(parent.id) : categories;
    setForm({ ...EMPTY, sort_order: siblings.length ? Math.max(...siblings.map(s => s.sort_order)) + 1 : 0 });
    setEditing({ mode: 'add', parent });
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY, ...row, description: row.description || '', image_url: row.image_url || '' });
    setEditing({ mode: 'edit', id: row.id, parent: rows.find(r => r.id === row.parent_id) || null });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing.mode === 'add') {
        await API.post('/brand-categories', { ...form, brand_id: brand.id, parent_id: editing.parent?.id || null });
      } else {
        await API.put(`/brand-categories/${editing.id}`, form);
      }
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (row) => {
    const subs = childrenOf(row.id).length;
    const msg = subs
      ? `Delete "${row.name}" and its ${subs} sub categor${subs === 1 ? 'y' : 'ies'}?`
      : `Delete "${row.name}"?`;
    if (!window.confirm(msg)) return;
    try { await API.delete(`/brand-categories/${row.id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  };

  const RowActions = ({ row }) => (
    <div className="flex gap-1.5 shrink-0">
      <button onClick={() => openEdit(row)} title="Edit"
        className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
        <PencilIcon className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => handleDelete(row)} title="Delete"
        className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const HiddenBadge = ({ row }) => !row.is_active
    ? <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-500">Hidden</span> : null;

  const formTitle = editing
    ? `${editing.mode === 'add' ? 'Add' : 'Edit'} ${editing.parent ? `Sub Category under "${editing.parent.name}"` : 'Category'}`
    : '';

  return (
    <Modal title={editing ? formTitle : `${brand.name} — Categories`} onClose={onClose} wide>
      {editing ? (
        <div className="space-y-4">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary">
            <ArrowLeftIcon className="w-3.5 h-3.5" /> Back to list
          </button>
          <div>
            <label className={labelClass}>Name *</label>
            <input value={form.name} autoFocus
              onChange={e => { const name = e.target.value; setForm(f => ({ ...f, name, slug: editing.mode === 'add' ? slugify(name) : f.slug })); }}
              placeholder={editing.parent ? 'e.g. Disc Cutting' : 'e.g. Machine Cutting Tools'} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Slug</label>
            <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
              className={`${inputClass} font-mono`} />
            <p className="text-xs text-gray-400 mt-1">The last part of the page address. Leave as generated unless you need to change it.</p>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3} className={`${inputClass} resize-none`} />
          </div>
          <div>
            <label className={labelClass}>Image</label>
            {form.image_url && (
              <div className="h-28 bg-gray-50 border border-gray-200 flex items-center justify-center mb-2 overflow-hidden">
                <img src={imgSrc(form.image_url)} alt="preview" className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
            <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="or paste image URL..." className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Sort Order</label>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))} className={inputClass}>
                <option value={1}>Active</option>
                <option value={0}>Hidden</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving || uploading || !form.name.trim()} className="btn-primary text-sm disabled:opacity-60 flex-1">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditing(null)} className="btn-outline text-sm flex-1">Cancel</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between gap-4 mb-5">
            <p className="text-xs text-gray-500">
              Visitors click <strong>{brand.name}</strong> → a category → a sub category → Contact Us.
            </p>
            <button onClick={() => openAdd()} className="btn-primary text-xs flex items-center gap-1.5 shrink-0">
              <PlusIcon className="w-3.5 h-3.5" /> Add Category
            </button>
          </div>

          {loading ? (
            <div className="h-24 bg-gray-50 animate-pulse" />
          ) : categories.length === 0 ? (
            <div className="border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400">
              No categories yet. Click "Add Category" to create one (e.g. Machine Cutting Tools).
            </div>
          ) : (
            <ul className="space-y-3">
              {categories.map(cat => (
                <li key={cat.id} className="border border-gray-200">
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-800 truncate">{cat.name}</span>
                      <HiddenBadge row={cat} />
                    </div>
                    <button onClick={() => openAdd(cat)}
                      className="text-xs text-primary hover:text-ink inline-flex items-center gap-1 shrink-0">
                      <PlusIcon className="w-3.5 h-3.5" /> Sub Category
                    </button>
                    <RowActions row={cat} />
                  </div>
                  {childrenOf(cat.id).length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      {childrenOf(cat.id).map(sub => (
                        <li key={sub.id} className="flex items-center gap-3 pl-9 pr-4 py-2.5">
                          <span className="text-gray-300">└</span>
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <span className="text-sm text-gray-700 truncate">{sub.name}</span>
                            <HiddenBadge row={sub} />
                          </div>
                          <RowActions row={sub} />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="pl-9 pr-4 py-2.5 text-xs text-gray-400">No sub categories — visitors see Contact Us on this category's page.</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
